import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';

export type SubscriptionTier = 'free' | 't25' | 't100' | 't200' | 't500';

export interface SeatUsage {
  tier: SubscriptionTier;
  max_seats: number;
  active_members: number;
  pending_invites: number;
  suspended: number;
  used_seats: number;
  can_add_seat: boolean;
}

export interface UseSeatUsage {
  tier: SubscriptionTier | null;
  maxSeats: number;
  activeMembers: number;
  pendingInvites: number;
  suspended: number;
  usedSeats: number;
  canAddSeat: boolean;
  loading: boolean;
  error: string | null;
  /** Re-run `org_seat_usage(p_org_id)`. Called automatically on
   *  `document.visibilitychange` (returning to the tab) so a tier change made
   *  in another tab or via Stripe webhook is reflected immediately. */
  refetch: () => Promise<void>;
}

interface SeatUsageRpcRow {
  tier?: string | null;
  max_seats?: number | null;
  active_members?: number | null;
  pending_invites?: number | null;
  suspended?: number | null;
  used_seats?: number | null;
  can_add_seat?: boolean | null;
}

function normalizeTier(t: string | null | undefined): SubscriptionTier | null {
  if (
    t === 'free' ||
    t === 't25' ||
    t === 't100' ||
    t === 't200' ||
    t === 't500'
  ) {
    return t;
  }
  return null;
}

/**
 * ViewModel-level wrapper around the `public.org_seat_usage(p_org_id)` RPC.
 * Returns a row containing tier + seat occupancy counts derived server-side
 * from the active memberships + non-revoked invitations. The RPC's
 * `can_add_seat` field is authoritative for the Invite-member gate; do NOT
 * recompute it client-side from the individual counts (the server-side check
 * mirrors the `create_invitation` enforcement and is the source of truth).
 *
 * Refetches on `document.visibilitychange` (returning to the tab) so the
 * counter stays fresh after a tier change made in another tab or via the
 * Stripe webhook.
 *
 * @param orgId — active org id; `null` parks the hook in `loading: false`
 *   with cleared counts so consumers can call it unconditionally.
 */
export function useSeatUsage(orgId: string | null): UseSeatUsage {
  const [tier, setTier] = useState<SubscriptionTier | null>(null);
  const [maxSeats, setMaxSeats] = useState(0);
  const [activeMembers, setActiveMembers] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [suspended, setSuspended] = useState(0);
  const [usedSeats, setUsedSeats] = useState(0);
  const [canAddSeat, setCanAddSeat] = useState(false);
  const [loading, setLoading] = useState<boolean>(!!orgId);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (id: string) => {
      const { data, error: rpcErr } = await supabase.rpc('org_seat_usage', {
        p_org_id: id
      });
      if (rpcErr) {
        setError("Couldn't load seat usage. Please try again.");
        setLoading(false);
        return;
      }
      // The RPC returns a setof row; PostgREST surfaces it as an array with
      // one entry. Be defensive — accept either the array form or a single
      // object form so future signature tweaks don't silently null us out.
      const rows = (Array.isArray(data) ? data : [data]) as ReadonlyArray<
        SeatUsageRpcRow | null | undefined
      >;
      const row = rows[0] ?? null;
      if (!row) {
        setError("Couldn't load seat usage. Please try again.");
        setLoading(false);
        return;
      }
      const t = normalizeTier(row.tier);
      setTier(t);
      setMaxSeats(typeof row.max_seats === 'number' ? row.max_seats : 0);
      setActiveMembers(
        typeof row.active_members === 'number' ? row.active_members : 0
      );
      setPendingInvites(
        typeof row.pending_invites === 'number' ? row.pending_invites : 0
      );
      setSuspended(typeof row.suspended === 'number' ? row.suspended : 0);
      setUsedSeats(typeof row.used_seats === 'number' ? row.used_seats : 0);
      setCanAddSeat(row.can_add_seat === true);
      setError(null);
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setLoading(false);
      setTier(null);
      setMaxSeats(0);
      setActiveMembers(0);
      setPendingInvites(0);
      setSuspended(0);
      setUsedSeats(0);
      setCanAddSeat(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    (async () => {
      await load(orgId);
      if (cancelled) {
        // No state writes after unmount — `load` may have already set state
        // before the cleanup fires; React 18's StrictMode tolerates this for
        // useState setters, which no-op on unmounted components.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, load]);

  useEffect(() => {
    if (!orgId) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Fire-and-forget — `load` swallows its own errors into `error` state.
        void load(orgId);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [orgId, load]);

  const refetch = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    await load(orgId);
  }, [orgId, load]);

  return {
    tier,
    maxSeats,
    activeMembers,
    pendingInvites,
    suspended,
    usedSeats,
    canAddSeat,
    loading,
    error,
    refetch
  };
}
